import React, { useMemo } from 'react';
import Icon from '../AppIcon';

const pad2 = (n) => String(n).padStart(2, '0');

const parseHHMM = (s) => {
  const m = String(s || '').trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  return { h: parseInt(m[1], 10), min: parseInt(m[2], 10) };
};

const formatHHMM = (mins) => {
  const total = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${pad2(h)}:${pad2(m)}`;
};

const toTeamLabel = (team) => {
  if (!Array.isArray(team)) return '';
  const names = team.map((p) => p?.name).filter(Boolean);
  return names.join(' / ');
};

const BracketResultRenderer = ({ generated, config }) => {
  const layout = config?.displayLayout || config?.display_layout || 'rounds_table';
  const gameMinutes = Math.max(1, parseInt(config?.gameMinutes ?? config?.overrideGameMinutes ?? 15, 10) || 15);
  const courtCount = Math.max(1, parseInt(config?.courtCount ?? 1, 10) || 1);

  const scheduleRows = useMemo(() => {
    const start = parseHHMM(config?.startTime || config?.start_time);
    const baseMinutes = start ? start.h * 60 + start.min : null;

    const rows = [];
    (generated?.rounds || []).forEach((round, roundIdx) => {
      const roundStart = baseMinutes === null ? null : baseMinutes + roundIdx * gameMinutes;
      (round?.matches || []).forEach((m, idx) => {
        const court = (idx % courtCount) + 1;
        rows.push({
          round: round?.round ?? roundIdx + 1,
          court,
          startAt: roundStart === null ? null : formatHHMM(roundStart),
          endAt: roundStart === null ? null : formatHHMM(roundStart + gameMinutes),
          left: toTeamLabel(m?.team1),
          right: toTeamLabel(m?.team2)
        });
      });
    });
    return rows;
  }, [config?.startTime, config?.start_time, courtCount, gameMinutes, generated?.rounds]);

  if (!generated) {
    return (
      <div className="bg-muted rounded-md border border-border p-4 text-sm caption text-muted-foreground">
        인원과 설정값을 입력한 뒤, 생성 버튼을 눌러 결과를 확인하세요.
      </div>
    );
  }

  if (layout === 'schedule') {
    return (
      <div className="space-y-3">
        <div className="bg-muted rounded-md border border-border p-3 flex items-start gap-2">
          <Icon name="CalendarClock" size={18} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-xs caption text-muted-foreground">
            {config?.startTime ? (
              <span>
                시작 시간 {config.startTime} 기준으로 라운드별 {gameMinutes}분 간격으로 배정합니다.
              </span>
            ) : (
              <span>
                시작 시간이 설정되지 않았습니다. 시간은 표시하지 않고 코트/순서만 보여줍니다.
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border border-border rounded-xl overflow-hidden">
            <thead className="bg-muted">
              <tr>
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-2 border-b border-border">라운드</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-2 border-b border-border">코트</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-2 border-b border-border">시간</th>
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-2 border-b border-border">경기</th>
              </tr>
            </thead>
            <tbody>
              {scheduleRows.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-sm text-muted-foreground" colSpan={4}>
                    일정이 없습니다.
                  </td>
                </tr>
              ) : (
                scheduleRows.map((r, i) => (
                  <tr key={`${r.round}-${r.court}-${i}`} className="bg-card">
                    <td className="px-3 py-2 text-sm text-foreground border-b border-border">{r.round}</td>
                    <td className="px-3 py-2 text-sm text-foreground border-b border-border">{r.court}</td>
                    <td className="px-3 py-2 text-sm text-foreground border-b border-border">
                      {r.startAt ? `${r.startAt} ~ ${r.endAt}` : '-'}
                    </td>
                    <td className="px-3 py-2 text-sm text-foreground border-b border-border">
                      <span className="text-muted-foreground">{r.left}</span>
                      <span className="mx-2 text-muted-foreground">vs</span>
                      <span className="text-muted-foreground">{r.right}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (layout === 'tree') {
    return (
      <div className="space-y-4">
        {(generated?.rounds || []).map((round, roundIdx) => (
          <div key={round?.round ?? roundIdx} className="border border-border rounded-xl overflow-hidden">
            <div className="bg-muted border-b border-border px-3 py-2 flex items-center justify-between">
              <span className="text-sm font-heading font-semibold text-foreground">라운드 {round?.round ?? roundIdx + 1}</span>
              <span className="text-xs caption text-muted-foreground">경기 {round?.matches?.length || 0}개</span>
            </div>

            <div className="p-3 space-y-3">
              {(round?.matches || []).length === 0 && (
                <div className="text-sm caption text-muted-foreground">이번 라운드에 표시할 경기가 없습니다.</div>
              )}

              {(round?.matches || []).map((m, idx) => (
                <div key={idx} className="bg-card border border-border rounded-xl p-3">
                  <div className="text-xs caption text-muted-foreground mb-2">매치 {idx + 1}</div>
                  <div className="space-y-2">
                    <div className="bg-muted/50 border border-border rounded-lg p-2">
                      <p className="text-sm font-medium text-foreground">{toTeamLabel(m?.team1)}</p>
                    </div>
                    <div className="bg-muted/50 border border-border rounded-lg p-2">
                      <p className="text-sm font-medium text-foreground">{toTeamLabel(m?.team2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(generated?.rounds || []).map((round) => (
        <div key={round.round} className="border border-border rounded-xl overflow-hidden">
          <div className="bg-muted border-b border-border px-3 py-2 flex items-center justify-between">
            <span className="text-sm font-heading font-semibold text-foreground">라운드 {round.round}</span>
            <span className="text-xs caption text-muted-foreground">경기 {round.matches?.length || 0}개</span>
          </div>

          <div className="p-3 space-y-2">
            {(round.matches || []).length === 0 && (
              <div className="text-sm caption text-muted-foreground">이번 라운드에 배정할 수 있는 경기가 없습니다.</div>
            )}

            {(round.matches || []).map((m, idx) => (
              <div key={idx} className="bg-card border border-border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs caption text-muted-foreground">코트 {idx + 1}</span>
                  <span className="text-xs caption text-muted-foreground">{gameMinutes}분</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="bg-muted rounded-md p-2">
                    <p className="text-sm font-medium text-foreground">{toTeamLabel(m?.team1)}</p>
                  </div>
                  <div className="bg-muted rounded-md p-2">
                    <p className="text-sm font-medium text-foreground">{toTeamLabel(m?.team2)}</p>
                  </div>
                </div>
              </div>
            ))}

            {round.resting?.length > 0 && (
              <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
                <p className="text-sm font-medium text-foreground">쉬는 인원</p>
                <p className="text-xs caption text-muted-foreground mt-1">
                  {round.resting.map((p) => p.name).join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BracketResultRenderer;
